/**
 * Shared color utilities for hex manipulation and contrast.
 */

/** Convert HSL (h: 0-360, s: 0-100, l: 0-100) to a hex color string. */
export function hslToHex(h: number, s: number, l: number): string {
  const sN = s / 100;
  const lN = l / 100;
  const a = sN * Math.min(lN, 1 - lN);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = lN - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color)
      .toString(16)
      .padStart(2, "0");
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

/** Return black or white hex for readable text on a given background.
 *
 * Uses CIELAB L* (perceptual lightness) — matches the backend's
 * `services/coloring/palette.py:font_for()` so client-side font picks agree
 * with anything the server pre-computes. Bright greens / yellows correctly
 * get black text where a naive sRGB luma threshold would mis-classify them.
 */
export function autoFontColor(hex: string): string {
  const h = hex.replace("#", "");
  if (h.length !== 6) return "#ffffff";

  const srgbToLinear = (c: number) => {
    const v = c / 255;
    return v > 0.04045 ? Math.pow((v + 0.055) / 1.055, 2.4) : v / 12.92;
  };

  const r = srgbToLinear(parseInt(h.slice(0, 2), 16));
  const g = srgbToLinear(parseInt(h.slice(2, 4), 16));
  const b = srgbToLinear(parseInt(h.slice(4, 6), 16));

  // sRGB → XYZ (D65) → normalize Y by reference white Yn=1.0
  const y = r * 0.2126729 + g * 0.7151522 + b * 0.0721750;
  // Y → L*
  const L = y > 0.008856 ? 116 * Math.pow(y, 1.0 / 3.0) - 16 : 903.3 * y;

  return L > 60 ? "#000000" : "#ffffff";
}

/** Check whether a string is a valid 7-char hex color (e.g. "#ab12cd"). */
export function isValidHex(value: string): boolean {
  return /^#[0-9a-fA-F]{6}$/.test(value);
}

/** Normalize a hex color to 7-char #rrggbb so `<input type="color">` renders it
 *  (it rejects #abc / named colors). Expands #abc → #aabbcc; falls back to grey. */
export function toHex7(value: string): string {
  const v = (value || "").trim();
  if (isValidHex(v)) return v;
  if (/^#[0-9a-fA-F]{3}$/.test(v)) {
    return "#" + v.slice(1).split("").map((ch) => ch + ch).join("");
  }
  return "#888888";
}

/** Stable hash of `prefix` → hex color via HSL (55% saturation, 55% lightness).
 *  Same input always returns the same color, which is useful for assigning
 *  default chip colors that survive bucket re-runs and reorderings. */
export function colorForPrefix(prefix: string): string {
  let hash = 0;
  for (let i = 0; i < prefix.length; i++) {
    hash = (hash * 31 + prefix.charCodeAt(i)) | 0;
  }
  const hue = Math.abs(hash) % 360;
  return hslToHex(hue, 55, 55);
}

/** The display swatch color for a work code: an explicit color when set, else a
 *  stable hue derived from the code/prefix, normalized to #rrggbb. */
export function swatchHex(color: string, key: string): string {
  return toHex7(color || colorForPrefix(key) || "#888888");
}

/** Hue (0-360) of a hex color, or null if it has no saturation / is unparseable. */
function hueOf(hex: string): number | null {
  const h = toHex7(hex).slice(1);
  if (h.length !== 6) return null;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;
  if (d < 0.02) return null; // grey — no meaningful hue
  let hue: number;
  if (max === r) hue = ((g - b) / d) % 6;
  else if (max === g) hue = (b - r) / d + 2;
  else hue = (r - g) / d + 4;
  hue *= 60;
  return (hue + 360) % 360;
}

/** Pick a work-code color maximally distinct (in hue) from the colors already
 *  in use this section. Finds the largest gap on the hue wheel and returns its
 *  midpoint, so consecutive new codes don't all land on near-identical hues
 *  (sequential numeric codes hash to adjacent hues otherwise). */
export function distinctSwatchHex(existing: Array<string | null | undefined>): string {
  const hues = existing
    .map((c) => (c ? hueOf(c) : null))
    .filter((h): h is number => h != null)
    .sort((a, b) => a - b);
  if (hues.length === 0) return hslToHex(210, 60, 52);
  let bestStart = hues[hues.length - 1];
  let bestGap = hues[0] + 360 - hues[hues.length - 1];
  for (let i = 1; i < hues.length; i++) {
    const gap = hues[i] - hues[i - 1];
    if (gap > bestGap) {
      bestGap = gap;
      bestStart = hues[i - 1];
    }
  }
  const hue = (bestStart + bestGap / 2) % 360;
  return hslToHex(hue, 60, 52);
}
